import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/common/state/hooks";
import { fetchAgent, RagAgent } from "src/features/rag/api/ragApi";
import RagChat from "src/features/rag/components/RagChat";
import RagConversationLoader from "src/features/rag/components/RagConversationLoader";
import {
  fetchConversationsThunk,
  openConversationThunk,
  startNewConversation,
  setActiveConversationId,
  getActiveRagConversation,
} from "src/features/rag/state/ragSlice";

// Wixarpedia is permanently bound to the shared "Sophie" RAG agent (id 3 —
// World famous Sophie from Wixar). There is intentionally no agent picker:
// the agent cannot be changed or unselected from this surface.
const SOPHIE_AGENT_ID = 3;

const WixarpediaChat: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [agent, setAgent] = useState<RagAgent | null>(null);
  const [loading, setLoading] = useState(true);
  const activeConversation = useSelector(getActiveRagConversation);

  // Bumped by "Nouvelle requête". Together with the active thread id it forms the
  // chat pane's React key — see chatKey below.
  const [newChatCounter, setNewChatCounter] = useState(0);

  const loadAgent = useCallback(async () => {
    try {
      const data = await fetchAgent(SOPHIE_AGENT_ID);
      setAgent(data);
    } catch (err) {
      console.error("Failed to load Wixarpedia agent:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgent();
  }, [loadAgent]);

  useEffect(() => {
    dispatch(fetchConversationsThunk());
  }, [dispatch]);

  const handleConversationSelect = (conversationId: number) => {
    dispatch(openConversationThunk(conversationId));
  };

  const handleNewConversation = () => {
    dispatch(startNewConversation());
    setNewChatCounter((n) => n + 1);
  };

  // The first question of an unsaved chat opens a thread server-side; adopt its
  // id (so follow-ups append to it) and refresh the sidebar so it appears.
  // Deliberately does NOT set `activeConversation`, which would remount the pane
  // via chatKey and wipe the answer still streaming into it.
  const handleConversationStarted = (conversationId: number) => {
    dispatch(setActiveConversationId(conversationId));
    dispatch(fetchConversationsThunk());
  };

  // Remounting is how a thread switch resets the pane: messages, input draft,
  // voice mode and dictation all reinitialise together, so nothing leaks from
  // the previous thread and history never reaches the text-to-speech path.
  const chatKey = activeConversation
    ? `conversation-${activeConversation.id}`
    : `new-${newChatCounter}`;

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-3">
          <div className="w-12 h-12 bg-gray-200 rounded-xl" />
          <div className="h-4 bg-gray-200 rounded w-32" />
        </div>
      </div>
    );
  }

  // Sophie is a curated shared agent; document_count drives the same
  // empty/disabled state RagChat uses on the Agents page.
  const hasDocuments = (agent?.document_count ?? 0) > 0;

  // Mirror the ScenarIA shell (ScenarioHelperChat) so the chat column lands
  // at the exact same width. The left column holds the conversation history;
  // the right one is an empty, borderless spacer (Wixarpedia has no agent
  // picker — the agent is fixed).
  return (
    <div
      className="grid h-full w-full overflow-hidden"
      style={{ gridTemplateColumns: "240px 1fr 280px" }}
    >
      <div className="bg-gray-100 px-3 pt-1 border-r border-gray-200 overflow-y-auto overflow-x-hidden">
        <RagConversationLoader
          onConversationSelect={handleConversationSelect}
          onNewConversation={handleNewConversation}
        />
      </div>

      <div className="min-w-0 overflow-hidden p-2">
        <div className="h-full bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col min-h-0">
          <RagChat
            key={chatKey}
            agentId={SOPHIE_AGENT_ID}
            hasDocuments={hasDocuments}
            showHeader={false}
            emptyText={t("pages.agents.chat.wixarpediaEmptyChat")}
            initialConversation={activeConversation}
            onConversationStarted={handleConversationStarted}
          />
        </div>
      </div>

      <div aria-hidden="true" />
    </div>
  );
};

export default WixarpediaChat;
